import type {
	AuthMessageApi,
	FirebaseAuthService,
	L10nTranslator,
	ParsedError,
} from "./types";

export const bindAuthServiceMessage = <TResponse extends ParsedError>(
	authService: FirebaseAuthService<TResponse>,
	message: AuthMessageApi
) => {
	const {
		getCurrentUser,
		getIdTokenWithExpiry,
		signOutUser,
		isValidUser,
		listenToAuthState,
		onIdTokenChange,
		generateMultiFactorSecret,
		generateMultiFactorQrCode,
		unEnrollMultiFactor,
		checkIfMultiFactorAuthIsEnrolled,
	} = authService;

	return {
		getCurrentUser,
		getIdTokenWithExpiry,
		signOutUser,
		isValidUser,
		listenToAuthState,
		onIdTokenChange,
		generateMultiFactorSecret,
		generateMultiFactorQrCode,
		unEnrollMultiFactor,
		checkIfMultiFactorAuthIsEnrolled,
		signInWithEmail: (
			email: string,
			password: string,
			remember: boolean,
			l10n: L10nTranslator
		) => authService.signInWithEmail(message, email, password, remember, l10n),
		signUpWithEmail: (
			email: string,
			password: string,
			username: string,
			remember: boolean,
			subscribe: boolean,
			l10n: L10nTranslator
		) =>
			authService.signUpWithEmail(
				message,
				email,
				password,
				username,
				remember,
				subscribe,
				l10n
			),
		sendUserPasswordResetEmail: (email: string, l10n: L10nTranslator) =>
			authService.sendUserPasswordResetEmail(message, email, l10n),
		resetPassword: (code: string, password: string, l10n: L10nTranslator) =>
			authService.resetPassword(message, code, password, l10n),
		changePassword: (
			oldPassword: string,
			newPassword: string,
			l10n: L10nTranslator
		) => authService.changePassword(message, oldPassword, newPassword, l10n),
		requestSendEmailVerification: (l10n: L10nTranslator) =>
			authService.requestSendEmailVerification(message, l10n),
		verifyEmail: (code: string, l10n: L10nTranslator) =>
			authService.verifyEmail(message, code, l10n),
		revert2fa: (code: string, l10n: L10nTranslator) =>
			authService.revert2fa(message, code, l10n),
		enrollMultiFactor: (
			totpSecret: unknown,
			verificationCode: string,
			l10n: L10nTranslator
		) =>
			authService.enrollMultiFactor(
				message,
				totpSecret,
				verificationCode,
				l10n
			),
		reAuthWithPassword: (password: string, l10n: L10nTranslator) =>
			authService.reAuthWithPassword(message, password, l10n),
		resolveTOTPCode: (code: string, error: unknown, l10n: L10nTranslator) =>
			authService.resolveTOTPCode(message, code, error, l10n),
	};
};
